import {
  ChangeEvent,
  Dispatch,
  MouseEvent,
  SetStateAction,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import queryHooks from '../../common/queryHooks/index.ts';
import {
  HydratedCustomerLocation,
  HydratedCustomerLocationAddress,
} from '../../common/types/compoundModels.ts';
import M from 'materialize-css';
import { Link } from 'react-router-dom';
import { sendPedigreeFinalReviewData } from './SendPedigree.tsx';

interface IProps {
  setCustomerLocationAddressId: Dispatch<SetStateAction<number | undefined>>;
  setFinalReviewData: Dispatch<SetStateAction<sendPedigreeFinalReviewData>>;
}

const CustomerLocationSelect = ({
  setCustomerLocationAddressId,
  setFinalReviewData,
}: IProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const selectRef = useRef<HTMLSelectElement>(null);

  const [selectedCustomerLocation, setSelectedCustomerLocation] =
    useState<HydratedCustomerLocation>();
  const [selectedCustomerLocationAddress, setSelectedCustomerLocationAddress] =
    useState<HydratedCustomerLocationAddress>();

  const { data: customerLocations } =
    queryHooks.customerLocation.useReadAllHydratedByUserId();

  useLayoutEffect(() => {
    const { current } = modalRef;
    if (!current) return;

    M.Modal.init(current);
    return () => {
      M.Modal.getInstance(current)?.destroy();
    };
  }, []);

  const getModal = () =>
    modalRef.current ? M.Modal.getInstance(modalRef.current) : null;

  useEffect(() => {
    if (!customerLocations?.[0]?.customerLocationAddresses) return;
    const location = customerLocations[0];

    setSelectedCustomerLocation(customerLocations[0]);
    setSelectedCustomerLocationAddress(
      location.customerLocationDefaultAddress ||
        location.customerLocationAddresses[0]
    );
    setFinalReviewData(prev => ({ ...prev, customerLocation: location }));
  }, [customerLocations]);

  useLayoutEffect(() => {
    const { current } = selectRef;
    if (!current) return;

    M.FormSelect.init(current);
    return () => {
      M.FormSelect.getInstance(current)?.destroy();
    };
  }, [customerLocations, selectedCustomerLocation]);

  const customerLocationChange = ({
    target: { value },
  }: ChangeEvent<HTMLSelectElement>) => {
    if (!value) {
      setSelectedCustomerLocationAddress(undefined);
      setFinalReviewData(prev => ({ ...prev, customerLocation: undefined }));
      return setSelectedCustomerLocation(undefined);
    }

    const id = parseInt(value);
    const customerLocation = customerLocations?.find(cl => cl.id === id);

    if (!customerLocation) {
      setSelectedCustomerLocationAddress(undefined);
      setFinalReviewData(prev => ({ ...prev, customerLocation: undefined }));
      return setSelectedCustomerLocation(undefined);
    }

    const { customerLocationGlobalLocationNumbers } = customerLocation;
    const verifiedGlns = customerLocationGlobalLocationNumbers?.filter(
      ({ isVerifiedForRouting }) => isVerifiedForRouting
    );
    setSelectedCustomerLocation(customerLocation);
    setFinalReviewData(prev => ({ ...prev, customerLocation }));

    if (!verifiedGlns.length) {
      setSelectedCustomerLocationAddress(undefined);
      setFinalReviewData(prev => ({ ...prev, customerLocation: undefined }));
      return;
    }

    setSelectedCustomerLocationAddress(
      customerLocation?.customerLocationDefaultAddress ||
        customerLocation?.customerLocationAddresses?.[0]
    );
  };

  const openChangeAddressModal = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    getModal()?.open();
  };

  useEffect(() => {
    setCustomerLocationAddressId(selectedCustomerLocationAddress?.id);
    setFinalReviewData(prev => ({
      ...prev,
      customerLocationAddress: selectedCustomerLocationAddress,
    }));
  }, [selectedCustomerLocationAddress]);

  return (
    <div className="card">
      <div className="card-content">
        <div className="row">
          <h6>Send From</h6>
          <div className="divider" />
        </div>
        <div className="row">
          <div className="input-field col s12 m4">
            <select
              ref={selectRef}
              id="customerLocationId"
              value={selectedCustomerLocation?.id}
              onChange={customerLocationChange}
            >
              <option value="">Select your location</option>
              {customerLocations?.filter(l => l.isActive).map(({ id, name }) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          </div>
          <div className="input-field col s12 m2 offset-m2">
            {selectedCustomerLocationAddress && (
              <>
                <p>{selectedCustomerLocationAddress.addressLineOne}</p>
                <p>{selectedCustomerLocationAddress.addressLineTwo}</p>
                {selectedCustomerLocationAddress.addressLineThree && (
                  <p>{selectedCustomerLocationAddress.addressLineThree}</p>
                )}
                <p>
                  {selectedCustomerLocationAddress.city},{' '}
                  {selectedCustomerLocationAddress.state.code}
                </p>
              </>
            )}
          </div>
          {selectedCustomerLocation?.customerLocationAddresses &&
          selectedCustomerLocation?.customerLocationAddresses.length > 1 &&
          selectedCustomerLocation?.customerLocationGlobalLocationNumbers?.filter(
            ({ isVerifiedForRouting }) => isVerifiedForRouting
          ).length ? (
            <div className="input-field col s12 m2 offset-m1">
              <a href="/" onClick={openChangeAddressModal}>
                Select a different address
              </a>
            </div>
          ) : null}
        </div>
        {selectedCustomerLocation && (
          <div className="row">
            {!selectedCustomerLocation.customerLocationGlobalLocationNumbers ? (
              <span>
                This location has no global location numbers. Add one{' '}
                <Link to="/gln">here</Link>
              </span>
            ) : !selectedCustomerLocation.customerLocationGlobalLocationNumbers.filter(
                ({ isVerifiedForRouting }) => isVerifiedForRouting
              ).length ? (
              <span className="col s12 m4 red-text">
                This location's global location numbers have not been verified.
                Please contact your Medtrax representative.
              </span>
            ) : null}
          </div>
        )}
      </div>
      <div ref={modalRef} className="modal">
        <div className="modal-content">
          <div className="row">
            <h6>Select an address</h6>
            <div className="divider" />
          </div>
          <div className="row">
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {selectedCustomerLocation?.customerLocationAddresses.map(
                address => {
                  const {
                    id,
                    addressLineOne,
                    addressLineTwo,
                    addressLineThree,
                    city,
                    state: { code },
                  } = address;

                  return (
                    <div
                      key={id}
                      className="card"
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        setSelectedCustomerLocationAddress(address);
                        getModal()?.close();
                      }}
                    >
                      <div className="card-content">
                        <div
                          style={{ display: 'flex', flexDirection: 'column' }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                            }}
                          >
                            <span>{addressLineOne}</span>
                            {id === selectedCustomerLocationAddress?.id && (
                              <span className="orange-text">selected</span>
                            )}
                          </div>
                          <span>{addressLineTwo}</span>
                          {addressLineThree && <span>{addressLineThree}</span>}
                          <span>
                            {city}, {code}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                }
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerLocationSelect;
